import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';

const API = 'https://api.gabrielny.com:8080/ai_zohoProxy.php';

const getName = (email) => {
    const prefix = email.split('@')[0].toLowerCase();
    if (prefix.includes('.')) {
        const first = prefix.split('.')[0];
        return first.charAt(0).toUpperCase() + first.slice(1);
    }
    return prefix.charAt(0).toUpperCase() + prefix.slice(1);
};

const TeamStatsModal = ({ onClose }) => {
    const [raw, setRaw] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [sortBy, setSortBy] = useState(null); // email to sort desc by

    useEffect(() => {
        fetch(`${API}?query=zoho_team_stats`)
            .then(r => r.json())
            .then(d => { setRaw(d); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    const stats = useMemo(() => {
        if (!raw) return null;

        console.log("RAW:", raw, typeof raw, Array.isArray(raw));

        const filtered = raw.filter(t => {
            const s = (t.Status || '').toLowerCase();
            if (filter === 'pending') return s === 'pending';
            if (filter === 'inserted') return s === 'approved' || s === 'rejected';
            return true;
        });

        const emailSet = new Set();
        const emailTotals = {};
        const matrix = {}; // { marketingType -> { email -> count } }

        filtered.forEach(t => {
            const email = t.GabrielEmployeeEmail;
            if (!email) return;
            emailSet.add(email);
            emailTotals[email] = (emailTotals[email] || 0) + 1;

            // [Other Assistance] row
            if (t.Other_Assistance === '1' || t.Other_Assistance === 1) {
                matrix['[Other Assistance]'] = matrix['[Other Assistance]'] || {};
                matrix['[Other Assistance]'][email] = (matrix['[Other Assistance]'][email] || 0) + 1;
            }

            // Marketing rows
            let marketing = t.Marketing;
            try { if (typeof marketing === 'string') marketing = JSON.parse(marketing); } catch { marketing = []; }
            if (!Array.isArray(marketing)) marketing = [];

            marketing.forEach(m => {
                if (!m || !m.trim()) return;
                matrix[m] = matrix[m] || {};
                matrix[m][email] = (matrix[m][email] || 0) + 1;
            });
        });

        let emails = [...emailSet].sort();

        // Sort columns by a specific person's count if clicked
        if (sortBy && emails.includes(sortBy)) {
            // already sorted, just put sortBy first
            emails = [sortBy, ...emails.filter(e => e !== sortBy)];
        }

        const rows = ['[Other Assistance]', ...Object.keys(matrix)
            .filter(k => k !== '[Other Assistance]')
            .sort()
        ].filter(k => matrix[k]);

        const grandTotal = filtered.length;

        return { emails, emailTotals, matrix, rows, grandTotal };
    }, [raw, filter, sortBy]);

    return (
        <div
            className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-[95vw] max-h-[95vh] flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 rounded-t-xl shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">Team Performance Matrix</h2>
                        <p className="text-xs text-gray-500 mt-0.5">Marketing activity breakdown by representative</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Filter toggle */}
                        <div className="flex bg-gray-200 p-1 rounded-lg text-xs gap-1">
                            {['all', 'pending', 'inserted'].map(f => (
                                <button
                                    key={f}
                                    onClick={() => setFilter(f)}
                                    className={`px-3 py-1 rounded-md transition-all capitalize ${
                                        filter === f ? 'bg-white shadow text-blue-600 font-semibold' : 'text-gray-600'
                                    }`}
                                >
                                    {f === 'inserted' ? 'Decided' : f.charAt(0).toUpperCase() + f.slice(1)}
                                </button>
                            ))}
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-auto p-4">
                    {loading ? (
                        <div className="flex items-center justify-center h-64 text-gray-400">Loading team data...</div>
                    ) : !stats ? (
                        <div className="text-red-500 p-4">Failed to load data.</div>
                    ) : (
                        <table className="w-full border-collapse text-sm">
                            <thead className="sticky top-0 z-10">
                            <tr>
                                {/* Marketing type column */}
                                <th className="bg-gray-800 text-white text-left px-4 py-2.5 font-semibold min-w-[220px] rounded-tl-lg">
                                    Marketing Type
                                </th>
                                {stats.emails.map((email, i) => (
                                    <th
                                        key={email}
                                        colSpan={2}
                                        onClick={() => setSortBy(sortBy === email ? null : email)}
                                        className={`bg-gray-800 text-white text-center px-3 py-2.5 font-semibold cursor-pointer hover:bg-gray-700 transition-colors select-none ${
                                            i === stats.emails.length - 1 ? 'rounded-tr-lg' : ''
                                        } ${sortBy === email ? 'bg-blue-700 hover:bg-blue-600' : ''}`}
                                    >
                                        <div className="text-sm">{getName(email)}</div>
                                        <div className="text-[10px] font-normal text-gray-300 mt-0.5">
                                            {stats.emailTotals[email] || 0} total
                                        </div>
                                    </th>
                                ))}
                                <th colSpan={2} className="bg-gray-900 text-white text-center px-3 py-2.5 font-semibold rounded-tr-lg">
                                    <div className="text-sm">Total</div>
                                    <div className="text-[10px] font-normal text-gray-300 mt-0.5">{stats.grandTotal}</div>
                                </th>
                            </tr>
                            {/* Sub-header */}
                            <tr className="bg-gray-100">
                                <td className="px-4 py-1 border-b border-gray-200 text-xs text-gray-400 italic">Category</td>
                                {stats.emails.map(email => (
                                    <React.Fragment key={email}>
                                        <td className="px-2 py-1 border-b border-gray-200 text-xs text-gray-400 text-center">#</td>
                                        <td className="px-2 py-1 border-b border-gray-200 text-xs text-gray-400 text-center">%</td>
                                    </React.Fragment>
                                ))}
                                <td className="px-2 py-1 border-b border-gray-200 text-xs text-gray-400 text-center">#</td>
                                <td className="px-2 py-1 border-b border-gray-200 text-xs text-gray-400 text-center">%</td>
                            </tr>
                            </thead>
                            <tbody>
                            {stats.rows.map((row, rowIdx) => {
                                const isOtherAssistance = row === '[Other Assistance]';
                                const rowTotal = Object.values(stats.matrix[row] || {}).reduce((a, b) => a + b, 0);

                                return (
                                    <tr
                                        key={row}
                                        className={`border-b border-gray-100 transition-colors ${
                                            isOtherAssistance
                                                ? 'bg-amber-50 hover:bg-amber-100'
                                                : rowIdx % 2 === 0
                                                    ? 'bg-white hover:bg-blue-50'
                                                    : 'bg-gray-50 hover:bg-blue-50'
                                        }`}
                                    >
                                        <td className={`px-4 py-2 font-medium whitespace-nowrap ${
                                            isOtherAssistance ? 'text-amber-800' : 'text-gray-700'
                                        }`}>
                                            {row}
                                        </td>
                                        {stats.emails.map(email => {
                                            const count = stats.matrix[row]?.[email] || 0;
                                            const pct = stats.emailTotals[email]
                                                ? ((count / stats.emailTotals[email]) * 100).toFixed(1)
                                                : '0.0';
                                            return (
                                                <React.Fragment key={email}>
                                                    <td className="px-3 py-2 text-center font-mono text-gray-800 font-medium">
                                                        {count > 0 ? count : <span className="text-gray-300">—</span>}
                                                    </td>
                                                    <td className="px-3 py-2 text-center text-gray-400 text-xs">
                                                        {count > 0 ? `${pct}%` : ''}
                                                    </td>
                                                </React.Fragment>
                                            );
                                        })}
                                        {/* Row total */}
                                        <td className="px-3 py-2 text-center font-mono font-bold text-gray-800 bg-gray-100">
                                            {rowTotal}
                                        </td>
                                        <td className="px-3 py-2 text-center text-xs text-gray-500 bg-gray-100">
                                            {stats.grandTotal ? `${((rowTotal / stats.grandTotal) * 100).toFixed(1)}%` : ''}
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                            {/* Totals footer */}
                            <tfoot>
                            <tr className="bg-gray-800 text-white font-bold">
                                <td className="px-4 py-3 rounded-bl-lg">Total</td>
                                {stats.emails.map(email => (
                                    <React.Fragment key={email}>
                                        <td className="px-3 py-3 text-center font-mono">
                                            {stats.emailTotals[email] || 0}
                                        </td>
                                        <td className="px-3 py-3 text-center text-xs text-gray-300">
                                            {stats.grandTotal
                                                ? `${(((stats.emailTotals[email] || 0) / stats.grandTotal) * 100).toFixed(1)}%`
                                                : ''}
                                        </td>
                                    </React.Fragment>
                                ))}
                                <td className="px-3 py-3 text-center font-mono text-blue-300">{stats.grandTotal}</td>
                                <td className="px-3 py-3 text-center text-xs text-gray-300 rounded-br-lg">100%</td>
                            </tr>
                            </tfoot>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TeamStatsModal;